import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Label } from '../ui/Label'
import { Button } from '../ui/Button'
import { maskSsn } from '../../lib/maskSsn'
import { activityLog } from '../../services/activityLog'

const AUTO_REMASK_MS = 10000

function revealSsn(value: string): string {
  return value
}

interface MaskedSsnFieldProps {
  label: string
  value: string
}

export const MaskedSsnField: React.FC<MaskedSsnFieldProps> = ({ label, value }) => {
  const [revealed, setRevealed] = useState(false)
  const autoRemaskTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scheduleAutoRemask = useCallback(() => {
    if (autoRemaskTimerRef.current) {
      clearTimeout(autoRemaskTimerRef.current)
    }
    autoRemaskTimerRef.current = setTimeout(() => {
      setRevealed(false)
    }, AUTO_REMASK_MS)
  }, [])

  const handleReveal = useCallback(() => {
    const next = !revealed
    setRevealed(next)
    if (next) {
      revealSsn(value)
      activityLog.addActivity({
        type: 'verification',
        title: 'SSN revealed',
        description: 'Tax ID (SSN/EIN) was viewed by the authenticated user.',
        status: 'completed',
      })
      scheduleAutoRemask()
    } else {
      if (autoRemaskTimerRef.current) {
        clearTimeout(autoRemaskTimerRef.current)
        autoRemaskTimerRef.current = null
      }
    }
  }, [revealed, value, scheduleAutoRemask])

  const handleBlur = useCallback(() => {
    if (revealed) {
      setRevealed(false)
      if (autoRemaskTimerRef.current) {
        clearTimeout(autoRemaskTimerRef.current)
        autoRemaskTimerRef.current = null
      }
    }
  }, [revealed])

  useEffect(() => {
    return () => {
      if (autoRemaskTimerRef.current) {
        clearTimeout(autoRemaskTimerRef.current)
      }
    }
  }, [])

  const displayValue = revealed ? revealSsn(value) : maskSsn(value)

  return (
    <div className="space-y-1">
      <Label className="text-xs text-purple-700 dark:text-purple-300">{label}</Label>
      <div
        className="flex items-center gap-2 w-full rounded-md border border-purple-100 dark:border-purple-900/40 bg-purple-50/30 dark:bg-purple-900/10 px-3 py-2"
        onBlur={handleBlur}
      >
        <span
          className="flex-1 text-sm text-slate-800 dark:text-slate-100 font-mono"
          aria-label={label}
          data-testid="masked-ssn-display"
        >
          {displayValue || <span className="text-slate-400 dark:text-slate-500 italic">—</span>}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 text-purple-500 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-200 flex-shrink-0"
          aria-label={revealed ? 'Hide SSN' : 'Show SSN'}
          onClick={handleReveal}
        >
          {revealed ? (
            <EyeOff className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Eye className="h-4 w-4" aria-hidden="true" />
          )}
        </Button>
      </div>
    </div>
  )
}
